import React, { useEffect, useState } from "react";
import { Col, Form, Modal, Row, Select, Button } from "antd";

import FormInput from "components/FormInput/FormInput";
import FormRadio from "components/FormRadio/FormRadio";
import FormSelection from "components/FormSelection/FormSelection";
import ClientJoinDateModal from "components/ClientJoinDateModal/ClientJoinDateModal";
import Locale from "react-intl-universal";

import { useFetchConstant } from "apis/constant";
const { useForm } = Form;
const { confirm } = Modal;

const defaultSelectedDisplay = [
  "deathBenefit",
  "tpdBenefit",
  "oldAgeDisabilityBenefit",
  "ciBenefit",
  "totalEarlyCIBenefit",
  "personalAccidentBenefit",
  "dailyBenefit",
  "medicalCardYearlyLimitAmt",
  "ciWaiver",
  "tpdWaiver",
];

const defaultSelection = [
  {
    value: "deathBenefit",
    label: "Death Benefit",
  },
  {
    value: "tpdBenefit",
    label: "Total Permanent Disability Benefit",
  },
  {
    value: "oldAgeDisabilityBenefit",
    label: "Old Age Disability Benefit",
  },
  {
    value: "ciBenefit",
    label: "Critical Illness",
  },
  {
    value: "totalEarlyCIBenefit",
    label: "Total/Early Critical Illness",
  },
  {
    value: "personalAccidentBenefit",
    label: "Personal Accident",
  },
  {
    value: "dailyBenefit",
    label: "Daily Benefits",
  },
  {
    value: "medicalCardYearlyLimitAmt",
    label: "Hospital And Surgical",
  },
  {
    value: "ciWaiver",
    label: "CI Waiver Premium",
  },
  {
    value: "tpdWaiver",
    label: "TP Waiver Permium",
  },
];

const getFormDefaultValues = (defaultStatus) => ({
  name: "",
  description: "",
  position: "",
  theme: {
    bgColor: "",
  },
  remarks: "",
  status: defaultStatus,
});

const generateOrderSelections = (maxPosition, isNew) => {
  const selectionLength = isNew ? maxPosition + 1 : maxPosition;
  const orderSelection = [];
  for (let i = 0; i < selectionLength; i++) {
    orderSelection.push({ label: i + 1, value: i });
  }
  return orderSelection;
};

const StatusSelect = ({ selections, status, onChange, disabled }) => {
  return (
    <Select
      value={status}
      style={{ width: 100, textAlign: "left" }}
      onChange={onChange}
      disabled={disabled}
    >
      {selections.map((selection) => (
        <Select.Option
          value={selection.value}
          key={selection.value}
          style={{ background: selection.value }}
        >
          {selection.label}
        </Select.Option>
      ))}
    </Select>
  );
};

const Footer = ({
  status,
  statusSelections,
  onDelete,
  onDuplicate,
  onSave,
  saveText,
  onStatusSelectionChange,
  isNew,
  isStatusSelectionDisabled,
  isDeleteButtonDisabled,
}) => {
  return (
    <Row justify={isNew ? "end" : "space-between"}>
      {!isNew && (
        <StatusSelect
          status={status}
          selections={statusSelections}
          onChange={onStatusSelectionChange}
          // disabled={isStatusSelectionDisabled}
        />
      )}
      <Col>
        {!isNew && (
          <Button
            type="ghost"
            onClick={onDuplicate}
            disabled={isStatusSelectionDisabled}
          >
            {Locale.get("proposal.duplicate")}
          </Button>
        )}
        {!isNew && (
          <Button danger onClick={onDelete} disabled={isDeleteButtonDisabled}>
            {Locale.get("proposal.delete")}
          </Button>
        )}
        <Button type="primary" onClick={onSave}>
          {saveText}
        </Button>
      </Col>
    </Row>
  );
};

const SettingsModal = ({
  values,
  adviseeDetails,
  visible,
  onSave,
  onClose,
  onDuplicate,
  isNew,
  maxPosition,
  proposalStatuses,
  proposalStatusesConst,
  onDelete,
  proposalCardThemes,
  ...props
}) => {
  const [form] = useForm();

  const {
    isLoading: isBenefitTypeloading,
    selection: benefitType,
  } = useFetchConstant("benefitTypes");
  const [displaySelection, setDisplaySelection] = useState([]);
  const [fieldValues, setFieldValues] = useState(
    getFormDefaultValues(proposalStatusesConst.ACTIVE.code)
  );
  const [
    isClientJoinDateModalVisible,
    setIsClientJoinDateModalVisible,
  ] = useState(false);

  const modalTitle = isNew
    ? Locale.get("proposal.createNewProposal")
    : Locale.get("proposal.proposalSetting");
  const isActiveProposal =
    fieldValues.status === proposalStatusesConst.ACTIVE.code;
  const isDealProposal = fieldValues.status === proposalStatusesConst.DEAL.code;

  const themesSelections =
    proposalCardThemes &&
    proposalCardThemes.map((theme) => ({
      value: theme.value,
      style: { backgroundColor: theme.value, width: 72 },
    }));

  useEffect(() => {
    const fieldValues = {
      ...getFormDefaultValues(proposalStatusesConst.ACTIVE.code),
      ...values,
      ...(isNew && { position: maxPosition }),
    };

    setFieldValues(fieldValues);
    form.setFieldsValue(fieldValues);

    if (!fieldValues.display || fieldValues.display.length === 0) {
      form.setFieldsValue({
        display: defaultSelectedDisplay,
      });
    }
  }, [form, values, isNew, maxPosition, proposalStatusesConst]);

  useEffect(() => {
    if (!values) {
      setDisplaySelection(defaultSelection);
      return;
    }

    let displaySelection = [];

    if (values.addedPolicyData) {
      // Merge available field
      for (const key in values.addedPolicyData) {
        let findCode = benefitType.filter((x) => x.code === key);

        if (findCode.length > 0) {
          if (key === "dailyHospitalizationBenefit") {
            displaySelection.push({
              value: key,
              label: `Female Only - ${findCode[0].label}`,
            });
          } else {
            displaySelection.push({
              value: key,
              label: findCode[0].label,
            });
          }
        } else {
          // Merge missing available field
          switch (key) {
            case "medicalCardYearlyLimitAmt":
              displaySelection.push({
                value: key,
                label: "Medical Card Yearly Limit",
              });
              break;
            case "medicalCardLifetimeLimitAmt":
              displaySelection.push({
                value: key,
                label: "Medical Card Life Time Limit",
              });
              break;
            case "medicalCardRoomAndBoardAmt":
              displaySelection.push({
                value: key,
                label: "Medical Card Room & Board Amount",
              });
              break;
            // case "extraCoverage":
            //   displaySelection.push({
            //     value: key,
            //     label: "Extra Coverage",
            //   });
            //   break;
            default:
              break;
          }
        }
      }

      // Merge additional field
      if (
        values.addedPolicyData.extraCoverage &&
        values.addedPolicyData.extraCoverage.length !== 0
      ) {
        for (let i = 0; i < values.addedPolicyData.extraCoverage.length; i++) {
          const extraCoverage = values.addedPolicyData.extraCoverage[i];
          displaySelection.push({
            value: `Extra-${extraCoverage.label}`,
            label: `Extra-${extraCoverage.label}`,
          });
        }
      }
    }

    setDisplaySelection(displaySelection);
  }, [benefitType, values]);

  const handleOnOk = async (e) => {
    e.preventDefault();

    const values = await form.validateFields();

    confirm({
      title: isNew
        ? Locale.get("proposal.saveTitle")
        : Locale.get("proposal.confirmSave"),
      content: Locale.get("proposal.confirmSaveContent"),
      cancelText: Locale.get("proposal.cancel"),
      okText: Locale.get("proposal.confirm"),
      onOk() {
        onSave({ ...values, status: fieldValues.status });
      },
    });
  };

  const handleOnDelete = () => {
    if (!isDealProposal) {
      confirm({
        title: Locale.get("proposal.confirmDelete"),
        content: Locale.get("proposal.confirmDeleteContent"),
        okText: Locale.get("proposal.delete"),
        okButtonProps: {
          danger: true,
        },
        onOk() {
          onDelete(fieldValues._id);
        },
      });
    }
  };

  const handleOnDuplicate = async () => {
    const payload = {
      position: maxPosition,
    };

    confirm({
      title: Locale.get("proposal.confirmDuplicate"),
      content: Locale.get("proposal.confirmDuplicateContent"),
      okText: Locale.get("proposal.duplicate"),
      okButtonProps: {
        danger: false,
      },
      onOk() {
        onDuplicate(fieldValues._id, payload);
      },
    });
  };

  const handleOnStatusSelectionChange = (value) => {
    const isDeal = value === proposalStatusesConst.DEAL.code;
    const content = isDeal
      ? Locale.get("proposal.dealContent")
      : Locale.get("proposal.archiveContent");
    const okText = isDeal
      ? Locale.get("proposal.dealProposal")
      : Locale.get("proposal.archiveProposal");

    switch (value) {
      case proposalStatusesConst.ACTIVE.code:
        confirm({
          title: `${Locale.get("proposal.aboutTo")} ${value} ${Locale.get(
            "proposal.thisProposal"
          )}`,
          onOk() {
            onSave({ ...fieldValues, status: value });
          },
        });
        break;

      case proposalStatusesConst.DEAL.code:
      case proposalStatusesConst.ARCHIVE.code:
        confirm({
          title: `${Locale.get("proposal.aboutTo")} ${value} ${Locale.get(
            "proposal.thisProposal"
          )}`,
          content,
          okText,
          cancelText: Locale.get("proposal.cancel"),
          okButtonProps: {
            danger: true,
          },
          onOk() {
            if (isDeal && !adviseeDetails.joinDate) {
              setIsClientJoinDateModalVisible(true);
            } else {
              onSave({ ...fieldValues, status: value });
            }
          },
        });
        break;

      default:
        break;
    }
  };

  const handleOnClientJoinDateModalOk = (joinDatePayload) => {
    onSave(
      { ...fieldValues, status: proposalStatusesConst.DEAL.code },
      joinDatePayload
    );
  };

  const handleOnClientJoinDateModalCancel = () => {
    setIsClientJoinDateModalVisible(false);
  };

  return (
    <Modal
      title={modalTitle}
      visible={visible}
      onOk={handleOnOk}
      onCancel={onClose}
      footer={
        <Footer
          status={fieldValues.status}
          statusSelections={proposalStatuses}
          proposalStatusesConst={proposalStatusesConst}
          onSave={handleOnOk}
          onDelete={handleOnDelete}
          onDuplicate={handleOnDuplicate}
          saveText={
            isNew
              ? Locale.get("proposal.CreateProposal")
              : Locale.get("proposal.save")
          }
          onStatusSelectionChange={handleOnStatusSelectionChange}
          isNew={isNew}
          isStatusSelectionDisabled={!isActiveProposal}
          isDeleteButtonDisabled={isDealProposal}
        />
      }
    >
      <Form form={form}>
        <Row gutter={16}>
          <Col span={24}>
            <FormInput
              label={Locale.get("proposal.proposalName")}
              name="name"
              placeholder={Locale.get("proposal.proposalNamePlaceholder")}
              requiredErrorMessage={Locale.get("proposal.errMsgProposalName")}
              disabled={!isActiveProposal}
            />
          </Col>
          <Col span={24}>
            <FormInput
              label={Locale.get("proposal.proposalDesc")}
              name="description"
              placeholder={Locale.get("proposal.descPlaceholder")}
              disabled={!isActiveProposal}
            />
          </Col>
          <Col span={24}>
            <FormSelection
              label={Locale.get("proposal.display")}
              name="display"
              placeholder={Locale.get("proposal.descPlaceholder")}
              selections={displaySelection}
              disabled={!isActiveProposal}
              isSearchable
              mandatory
              extraProps={{
                mode: "multiple",
              }}
            />
          </Col>
          <Col span={24}>
            <FormRadio
              label={Locale.get("proposal.cardColor")}
              name={["theme", "bgColor"]}
              selections={themesSelections}
              requiredErrorMessage={Locale.get("proposal.errMsgCardColor")}
              disabled={!isActiveProposal}
            />
          </Col>
          <Col span={24}>
            <FormSelection
              label={Locale.get("proposal.position")}
              name="position"
              selections={generateOrderSelections(maxPosition, isNew)}
              disabled={!isActiveProposal}
            />
          </Col>
          <Col span={24}>
            <FormInput
              label={Locale.get("proposal.remark")}
              name="remarks"
              placeholder={Locale.get("proposal.remarkPlaceholder")}
              type="textArea"
            />
          </Col>
        </Row>
      </Form>
      <ClientJoinDateModal
        visible={isClientJoinDateModalVisible}
        onOk={handleOnClientJoinDateModalOk}
        onCancel={handleOnClientJoinDateModalCancel}
      />
    </Modal>
  );
};

export default SettingsModal;
